// Main application entry point
import React, { useMemo, useEffect } from "react";
import { RestAuthProvider, useRestAuth } from "../providers/RestAuthProvider";
import { ToastProvider } from "../providers/ToastProvider";
import { LocalizationProvider, useLocalization } from "../providers/LocalizationProvider";
import AdminShell from "../components/layout/AdminShell";
import LoginForm from "../components/layout/LoginForm";
// import { createInMemoryDataSource } from "../lib/datasource/memory"; // Disabled - kept as example only
import { createDataSource } from "../lib/datasource/source";
import { logger } from "../lib/utils/logger";

// Inner app component that handles user authentication and language setup
function InnerApp({ data }) {
  const { user } = useRestAuth();
  const { setLanguageFromUser } = useLocalization();
  
  // Automatically set language when user changes
  useEffect(() => {
    logger.debug('App.jsx useEffect triggered', { 
      hasUser: !!user, 
      hasData: !!data, 
      hasState: !!(data && data.state), 
      hasUsers: !!(data && data.state && data.state.users),
      userCommonLanguage: user?.commonLanguage
    });
    
    if (user && user.commonLanguage) {
      // User already has language preference from RestAuthProvider
      logger.debug('Setting language directly from user object', { language: user.commonLanguage });
      setLanguageFromUser(user);
    }
  }, [user, setLanguageFromUser]);

  // Also try to set language when data state changes (in case users load after user login)
  useEffect(() => {
    if (user && data && data.state && data.state.users && data.state.users.length > 0) {
      const completeUser = data.state.users.find(u => u.id === user.id);
      if (completeUser && completeUser.commonLanguage) {
        logger.debug('Setting language from data state change', { language: completeUser.commonLanguage });
        setLanguageFromUser(completeUser);
      }
    }
  }, [data?.state?.users, user, setLanguageFromUser]);
  
  // Show admin shell if user is logged in, otherwise show login form
  return user ? <AdminShell data={data} /> : <LoginForm />;
}

// Main App component that sets up the provider hierarchy
export default function App(){
  //const data = useMemo(()=>createSqlDataSource("/api"),[]); // Pour utiliser le SQL pour PostgreSQL
  //const data = useMemo(()=>createInMemoryDataSource(),[]); // Pour utiliser la mémoire pour l'instant
  
  return (
    <LocalizationProvider>
      <ToastProvider>
        <RestAuthProvider>
          <AppWithDataSource />
        </RestAuthProvider>
      </ToastProvider>
    </LocalizationProvider>
  );
}

// Component that creates and manages the data source
function AppWithDataSource() {
  const { user, token } = useRestAuth();
  const { language, userLanguageDetermined } = useLocalization();
  
  // Create data source when user is authenticated
  const data = useMemo(() => {
    if (user && token) {
      // Use API data source when authenticated
      logger.info("Creating data source for authenticated user", { userId: user.id });
      return createDataSource({ user, token });
    } else {
      // No fallback data source - user must be authenticated
      logger.info("No data source created for unauthenticated user");
      return null;
    }
  }, [user, token]);

  // Update data source language when language changes
  useEffect(() => {
    if (data && data.updateLanguage) {
      data.updateLanguage(language);
    }
  }, [data, language]);

  // Load initial data when data source changes AND user language is determined
  useEffect(() => {
    if (data && data.reload && userLanguageDetermined) {
      logger.info("Loading initial data with language", { language, userLanguageDetermined, isAdmin: user?.role === 'admin' });
      data.reload().catch(error => {
        logger.error("Failed to load initial data", error);
        // If API fails and we're using API data source, fall back to memory data source
        if (user && token && error.message?.includes('401')) {
          logger.warn("API authentication failed, this might indicate the API server is not running or token is invalid");
        }
      });
    }
  }, [data, language, userLanguageDetermined, user, token]);

  return <InnerApp data={data} />;
}